import { useCancelRequestHook } from '@op/hooks/useCancelRequest';
import {
  getKeywordDrillStats,
  getUrlDrillEngagement,
  getUrlDrillTaskList,
  getUrlDrillViewTimeline,
  requestAiTaskList,
  updateSummaryName,
} from '@op/services';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { dealWithKeywordDrillStats } from '../utils';
import { Button, Form, Modal, notification } from 'tea-component';
import { DBO_REQ, DBO_V } from '@op/utils/format';
import { GPTContentType } from '../types';
import useLanguage from '@op/hooks/useLanguage';
import { GPTSummaryNameForm } from '@op/components/GPT/PromptModal/PromptForm/GPTSummaryName';
import { useDeleteSummaryItem } from '../../hooks';
import { getLatestTimestamp } from '@op/utils/time';
import moment from 'moment';

export function useSummaryKeywordDrillStats({ filter }: any) {
  const [stats, setStats] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();
  function requestStats() {
    if (!filter.edition_unified_id) return;
    setError(false);
    setStats({});
    wrapperRequest((cancelFunc) =>
      getKeywordDrillStats(filter, cancelFunc)
        .then((data: any) => {
          const formattedStats = dealWithKeywordDrillStats({ data, filter });
          setStats(formattedStats);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  useEffect(() => {
    requestStats();
  }, [filter]);

  return { data: stats, loading, error, refetch: requestStats };
}

export function useSummaryUserSummary({
  filter,
  clearPageInfo,
  needRequest,
}: any) {
  const [data, setData] = useState<any>({});
  const [error, setError] = useState(false);
  const { loading, wrapperRequest } = useCancelRequestHook();

  function requestSummaryList() {
    if (!filter?.edition_unified_ids) return;
    setError(false);
    setData({});
    wrapperRequest((cancelFunc) =>
      requestAiTaskList(filter, cancelFunc)
        .then((data: any) => {
          const formattedStats = data || {};
          setData(formattedStats);
        })
        .catch(() => {
          setError(true);
        }),
    );
  }

  const { deleteKeywordAndUrlSummary } = useDeleteSummaryItem({
    thenCallback: clearPageInfo,
  });

  useEffect(() => {
    if (needRequest) {
      setData({});
      requestSummaryList();
    }
  }, [filter, needRequest]);

  return {
    data,
    loading,
    error,
    requestSummaryList,
    deleteSummary: deleteKeywordAndUrlSummary,
  };
}

export type UrlPercentType = {
  url: string;
  status: 'loading' | 'success' | 'error';
  count: number;
  comment_time?: string;
};

export function useUrlState({
  filter,
  taskId,
  setFilterLoading,
  isPreviews,
  gameInfo,
  taskData,
  gptList,
  setAllCommentsFilter,
  setShowCheckbox,
}: {
  filter: any;
  taskId?: number;
  setFilterLoading?: (value: boolean) => void;
  isPreviews?: boolean;
  gameInfo: any;
  taskData?: any;
  gptList: { current: GPTContentType[] };
  setAllCommentsFilter: (filter: any) => void;
  setShowCheckbox: (show: boolean) => void;
}) {
  const [isError, setIsError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [percent, setPercent] = useState(
    isPreviews ? Math.floor(Math.random() * 11) + 60 : 0,
  );
  const timer = useRef<any>(null);
  const [gptTaskList, setGptTaskList] = useState<any>({});
  const [percentList, setPercentList] = useState<Array<UrlPercentType>>([]);

  const { isSingleUrl, contentUrls } = useMemo(() => {
    const contentUrls = filter?.content_urls || [];
    return {
      isSingleUrl: contentUrls?.length === 1,
      contentUrls,
    };
  }, [filter]);

  const { handleStop, wrapperRequestTile } = useCancelRequestHook();

  function requestTaskInfo() {
    wrapperRequestTile(getUrlDrillTaskList, { id: taskId, ...gameInfo }).then(
      (data) => {
        let {
          task_data,
          url_data,
          urls_max_comment_time,
          urls_max_daily_view_date,
          urls_min_comment_time,
        } = data || {};
        setPercent((value: number) => {
          return Math.min(value + 1, 99);
        });
        let isFinished = true;
        const percentList = contentUrls.map((key: string) => {
          const {
            count = 0,
            status = 'loading',
            comment_time,
          } = url_data?.[key] || {};
          if (!['failed', 'success'].includes(status) && isFinished) {
            isFinished = false;
          }

          let realStatus = status;
          if (status === 'failed') {
            realStatus = 'error';
          }

          return {
            url: key,
            status: realStatus,
            count,
            comment_time,
          };
        });
        setIsError(percentList.every((item: any) => item.status === 'error'));
        setPercentList(percentList);
        if (urls_min_comment_time && moment(urls_min_comment_time).isValid()) {
          setAllCommentsFilter({
            start_time: urls_min_comment_time,
            end_time: getLatestTimestamp(
              urls_max_comment_time,
              urls_max_daily_view_date,
            ),
          });
          setShowCheckbox(true);
        }
        // 完成后不在请求
        if (isFinished) {
          setLoading(false);
          // 非预览这里开始可以点击
          !isPreviews && setFilterLoading?.(false);
        }
        // 修改筛选器不自动请求， 获取不到task_id
        if (!task_data?.comments_task) {
          task_data = {
            ...task_data,
            comments_task: {
              task_id: '',
              error_code: '80000',
            },
          };
        }
        if (!task_data?.video_task) {
          task_data = {
            ...task_data,
            video_task: {
              task_id: '',
              error_code: '80000',
            },
          };
        }
        const localTaskData = taskData || task_data || {};
        const localTaskDataKeys = Object.keys(localTaskData);

        let taskIdSuccess = !(gptList.current.length > 0);

        if (localTaskDataKeys.length > 0) {
          taskIdSuccess = localTaskDataKeys.every(
            (key: string) =>
              !!localTaskData?.[key]?.task_id ||
              !!localTaskData?.[key]?.error_code,
          );
        }

        setGptTaskList(localTaskData);

        // 预览这里开始可以点击
        if (isPreviews && taskIdSuccess && isFinished) {
          setFilterLoading?.(false);
        }

        if (!isFinished || !taskIdSuccess) {
          timer.current = setTimeout(() => {
            requestTaskInfo();
          }, 10000);
        }
      },
    );
  }

  function stop() {
    clearTimeout(timer.current);
    handleStop();
    setFilterLoading?.(false);
    setLoading(false);
  }

  useEffect(() => {
    if (DBO_V.is_empty(taskId)) return;
    setFilterLoading?.(true);
    setLoading(true);
    requestTaskInfo();
    return () => stop();
  }, [taskId]);

  return {
    loading,
    percent,
    isSingleUrl,
    percentList,
    setLoading,
    stop,
    gptTaskList,
    isError,
  };
}

export function useUrlChartInfo({
  filter,
  isSingleUrl,
  singleName,
  type,
  setApiLoading,
}: {
  filter: any;
  isSingleUrl: boolean;
  singleName: string;
  type: 'views' | 'comments';
  setApiLoading?: (item: string) => void;
}) {
  const [totalInfo, setTotalInfo] = useState<{
    total: number;
    positive: number;
    negative: number;
    commentsTime: string;
  }>({ total: 0, positive: 0, negative: 0, commentsTime: '' });
  const [loading, setLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [chartInfo, setChartInfo] = useState<{
    xAxis: Array<string>;
    series: Array<{ name: string; data: Array<number> }>;
    legend: Array<string>;
    totalSeries: Array<number>;
  }>({ xAxis: [], series: [], legend: [], totalSeries: [] });
  const [tableRecord, setTableRecord] = useState<Array<any>>([]);

  function requestData() {
    setLoading(true);
    setTotalInfo({ total: 0, positive: 0, negative: 0, commentsTime: '' });
    setIsEmpty(true);
    setChartInfo({ xAxis: [], series: [], legend: [], totalSeries: [] });

    getUrlDrillViewTimeline({ ...filter, timeline_type: type })
      .then((data: any) => {
        const {
          timeline,
          count,
          positive_count,
          negative_count,
          timeline_of_start_time,
          timeline_of_end_time,
          count_of_time,
        } = data || {};

        const { isEmpty, legend, series, tableRecord, ...props } =
          DBO_REQ.moreKeys({
            timeline,
            timeline_of_start_time,
            timeline_of_end_time,
            filter,
            needTable: true,
          });

        setTotalInfo({
          total: DBO_V.abs(count),
          positive: DBO_V.abs(positive_count),
          negative: DBO_V.abs(negative_count),
          commentsTime: count_of_time,
        });
        setChartInfo({ ...props, legend, series });
        setTableRecord(tableRecord);
        setIsEmpty(isEmpty);
      })
      .finally(() => {
        setLoading(false);
        setApiLoading?.(`chart_${type}`);
      });
  }

  useEffect(() => {
    requestData();
  }, [filter]);

  return {
    loading,
    totalInfo,
    chartInfo,
    isEmpty,
    tableRecord,
  };
}

export function useUrlEngagementInfo({
  filter,
  setApiLoading,
}: {
  filter: any;
  setApiLoading?: (item: string) => void;
}) {
  const [total, setTotal] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [isEmpty, setIsEmpty] = useState(true);
  const [postEngagement, setPostEngagement] = useState<any>({});
  const [commentsEngagement, setCommentsEngagement] = useState<any>({});

  function requestData() {
    setLoading(true);
    setTotal(0);
    setIsEmpty(true);

    getUrlDrillEngagement(filter)
      .then((data: any) => {
        const { posts, comments } = data || {};
        const {
          engagement = 0,
          likes = 0,
          dislikes = 0,
          comments: postComments = 0,
          shares = 0,
        } = posts || {};

        const {
          engagement: commentsEngagement = 0,
          positive_engagement = 0,
          negative_engagement = 0,
        } = comments || {};

        setPostEngagement({
          total: engagement,
          likes_ratio: DBO_V.percentageValue(likes, engagement, 2),
          dislikes_ratio: DBO_V.percentageValue(dislikes, engagement, 2),
          comments_ratio: DBO_V.percentageValue(postComments, engagement, 2),
          shares_ratio: DBO_V.percentageValue(shares, engagement, 2),
          likes,
          dislikes,
          comments: postComments,
          shares,
        });

        setCommentsEngagement({
          total: commentsEngagement,
          positive:
            DBO_V.percentageValue(positive_engagement, commentsEngagement, 6) *
            100,
          negative:
            DBO_V.percentageValue(negative_engagement, commentsEngagement, 6) *
            100,
        });

        setIsEmpty(isEmpty);
      })
      .finally(() => {
        setLoading(false);
        setApiLoading?.('engagement');
      });
  }

  useEffect(() => {
    requestData();
  }, [filter]);

  return {
    loading,
    total,
    isEmpty,
    postEngagement,
    commentsEngagement,
  };
}

function EditSummaryName({
  toggleWindows,
  title,
  id,
  setTitle,
  type,
  dataType,
}: {
  toggleWindows: () => void;
  title: string;
  id: number;
  setTitle: (value: string) => void;
  type:
    | 'common'
    | 'keyword'
    | 'url'
    | 'keyword_topic'
    | 'repeat_summary_task'
    | 'repeat_keyword_analysis_keyword_task'
    | 'repeat_keyword_analysis_topic_task';
  dataType?: 'new' | 'legacy';
}) {
  const [name, setName] = useState(title);
  const { formatMessage } = useLanguage();
  const [loading, setLoading] = useState(false);

  function update() {
    if (name.trim()) {
      setLoading(true);
      updateSummaryName({ name: name.trim(), id, type, data_type: dataType })
        .then(() => {
          setTitle(name);
          notification.success({
            description: formatMessage({ id: 'Success message prompt' }),
          });
        })
        .catch(() => {
          notification.error({
            description: formatMessage({ id: 'Failure Information Prompt' }),
          });
        })
        .finally(() => {
          setLoading(false);
          toggleWindows();
        });
    }
  }

  return (
    <Modal
      visible={true}
      caption={formatMessage({ id: 'Edit Name' })}
      onClose={() => toggleWindows()}
      className="dbo-edit-summaryName-modal"
    >
      <Modal.Body>
        <Form>
          <GPTSummaryNameForm
            name={name}
            setName={setName}
            needSummaryName={true}
          ></GPTSummaryNameForm>
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          disabled={!name.trim() || loading}
          onClick={() => update()}
        >
          {formatMessage({ id: 'Confirm' })}
        </Button>
        <Button
          type="weak"
          onClick={() => {
            toggleWindows();
          }}
        >
          {formatMessage({ id: 'Cancel' })}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export function useUpdateSummaryName() {
  const [title, setTitle] = useState('');
  const [visible, setVisible] = useState(false);

  const toggleWindows = () => {
    setVisible(!visible);
  };

  return {
    visible,
    title,
    setTitle,
    toggleWindows,
    EditSummaryName,
  };
}
